import React, { useEffect, useState, useCallback, useRef } from 'react';
import { RefreshCw, Warehouse, Printer, PackageCheck, History, Box, ScanLine } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const inr = (n) =>
  `₹${(Number(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('en-IN'); } catch { return iso; }
};

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('session_token')}` });

// Render the "Warehouse Transfer" summary as a printable browser window.
const printWarehouseTransfer = (data) => {
  const win = window.open('', '_blank');
  if (!win) {
    toast.error('Popup blocked — allow popups to print the Warehouse Transfer');
    return;
  }
  const addr = (a) =>
    `<div><strong>${a?.name || ''}</strong><br/>${(a?.lines || []).join('<br/>')}${
      a?.gst ? `<br/><span class="gst">GST: ${a.gst}</span>` : ''
    }</div>`;
  const rows = (data.boxes || [])
    .map(
      (b, i) => `<tr>
        <td>${i + 1}</td>
        <td>${b.box_number || ''}</td>
        <td>${b.external_box_no || '—'}</td>
        <td>${b.pick_slip_number || ''}</td>
        <td class="num">${(b.total_qty || 0).toLocaleString('en-IN')}</td>
        <td class="num">${inr(b.value)}</td>
      </tr>`
    )
    .join('');
  win.document.write(`<!doctype html><html><head><meta charset="utf-8"/>
    <title>Warehouse Transfer ${data.transfer_id || ''}</title>
    <style>
      * { box-sizing: border-box; }
      body { font-family: Arial, Helvetica, sans-serif; color:#1a1f2e; margin:24px; }
      h1 { font-size:20px; margin:0 0 4px; }
      .meta { font-size:12px; color:#555; margin-bottom:16px; }
      .addr-wrap { display:flex; justify-content:space-between; gap:24px; margin:16px 0 24px; font-size:12px; }
      .addr-box { border:1px solid #ddd; padding:10px 14px; border-radius:6px; width:48%; }
      .addr-box .lbl { font-size:10px; letter-spacing:.08em; text-transform:uppercase; color:#888; margin-bottom:6px; }
      .addr-box .gst { display:inline-block; margin-top:6px; font-weight:bold; }
      table { width:100%; border-collapse:collapse; font-size:12px; }
      th, td { border:1px solid #ccc; padding:6px 8px; text-align:left; }
      th { background:#f2efe9; }
      td.num, th.num { text-align:right; }
      tfoot td { font-weight:bold; background:#faf8f4; }
      .sign { margin-top:48px; display:flex; justify-content:space-between; font-size:12px; }
      .sign div { border-top:1px solid #333; padding-top:6px; width:40%; text-align:center; }
      @media print { body { margin:12mm; } }
    </style></head><body>
    <h1>Warehouse Transfer</h1>
    <div class="meta">
      Ref: <strong>${data.transfer_id || ''}</strong> &nbsp;•&nbsp;
      Date: ${fmtDate(data.generated_at)} &nbsp;•&nbsp;
      By: ${data.transferred_by || ''}
    </div>
    <div class="addr-wrap">
      <div class="addr-box"><div class="lbl">From</div>${addr(data.from_address)}</div>
      <div class="addr-box"><div class="lbl">To</div>${addr(data.to_address)}</div>
    </div>
    <table>
      <thead><tr>
        <th>#</th><th>Box No</th><th>Small-Box</th><th>Pick Slip</th><th class="num">Qty</th><th class="num">Value</th>
      </tr></thead>
      <tbody>${rows}</tbody>
      <tfoot><tr>
        <td colspan="4">Total (${data.box_count || 0} box${data.box_count === 1 ? '' : 'es'})</td>
        <td class="num">${(data.total_qty || 0).toLocaleString('en-IN')}</td>
        <td class="num">${inr(data.total_value)}</td>
      </tr></tfoot>
    </table>
    <div class="sign"><div>Dispatched By</div><div>Received By (Storage)</div></div>
    </body></html>`);
  win.document.close();
  win.focus();
  setTimeout(() => { try { win.print(); } catch (e) { /* noop */ } }, 350);
};

export const SeasonalBoxesTab = () => {
  const [view, setView] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [boxes, setBoxes] = useState([]);
  const [summary, setSummary] = useState({ count: 0, total_qty: 0, total_value: 0 });
  const [selected, setSelected] = useState({});
  const [transferring, setTransferring] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historySearch, setHistorySearch] = useState('');
  const [scanValue, setScanValue] = useState('');
  const scanRef = useRef(null);
  const scanTimerRef = useRef(null);
  const selectedRef = useRef(selected);
  useEffect(() => { selectedRef.current = selected; }, [selected]);

  const fetchPending = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API}/api/wms/packing/seasonal-boxes`, { headers: authHeaders() });
      if (!res.ok) throw new Error('fetch failed');
      const data = await res.json();
      setBoxes(data.boxes || []);
      setSummary({ count: data.count || 0, total_qty: data.total_qty || 0, total_value: data.total_value || 0 });
      setSelected({});
    } catch (e) {
      toast.error('Failed to load seasonal boxes');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      setHistoryLoading(true);
      const res = await fetch(`${API}/api/wms/packing/seasonal-boxes/transferred`, { headers: authHeaders() });
      if (!res.ok) throw new Error('fetch failed');
      const data = await res.json();
      setHistory(data.transfers || []);
    } catch (e) {
      toast.error('Failed to load transfer history');
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => { fetchPending(); }, [fetchPending]);
  useEffect(() => { if (view === 'history') fetchHistory(); }, [view, fetchHistory]);
  useEffect(() => () => { if (scanTimerRef.current) clearTimeout(scanTimerRef.current); }, []);

  const selectedIds = boxes.map((b) => b.box_id).filter((id) => selected[id]);
  const allChecked = boxes.length > 0 && selectedIds.length === boxes.length;

  const toggleAll = () => {
    if (allChecked) { setSelected({}); return; }
    const next = {};
    boxes.forEach((b) => { next[b.box_id] = true; });
    setSelected(next);
  };

  const toggleOne = (id) => setSelected((prev) => ({ ...prev, [id]: !prev[id] }));

  // Match a scanned/typed code to a pending box and auto-tick its checkbox.
  // Accepts either the system Box No OR the small-box code captured at packing
  // (external_box_no), since operators scan the physical small-box sticker.
  const processScan = useCallback((raw) => {
    const code = (raw || '').trim();
    if (!code) return;
    const norm = (s) => String(s || '').trim().toUpperCase();
    const box = boxes.find((b) => norm(b.box_number) === norm(code) || norm(b.external_box_no) === norm(code));
    if (!box) {
      toast.error(`Box "${code}" is not in the pending list`);
    } else if (selectedRef.current[box.box_id]) {
      toast.info(`Box ${box.box_number} is already selected`);
    } else {
      // Keep the ref current so back-to-back scans don't double-toast/miss.
      selectedRef.current = { ...selectedRef.current, [box.box_id]: true };
      setSelected((prev) => ({ ...prev, [box.box_id]: true }));
      toast.success(`Added ${box.box_number}`);
    }
    setScanValue('');
    setTimeout(() => scanRef.current?.focus(), 0);
  }, [boxes]);

  // Auto-submit each scan (no Enter needed): a hardware scanner fires the value
  // as a fast burst, so we debounce and process once the burst stops. Pressing
  // Enter (or clicking "Add") still submits immediately.
  const onScanChange = (e) => {
    const val = e.target.value;
    setScanValue(val);
    if (scanTimerRef.current) clearTimeout(scanTimerRef.current);
    if (val.trim()) {
      scanTimerRef.current = setTimeout(() => processScan(val), 150);
    }
  };

  const handleScan = (e) => {
    e.preventDefault();
    if (scanTimerRef.current) clearTimeout(scanTimerRef.current);
    processScan(scanRef.current?.value ?? scanValue);
  };

  const doTransfer = async (boxIds) => {
    if (!boxIds.length) { toast.error('Select at least one box'); return; }
    try {
      setTransferring(true);
      const res = await fetch(`${API}/api/wms/packing/seasonal-boxes/transfer-to-storage`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ box_ids: boxIds }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'transfer failed');
      toast.success(`Transferred ${data.box_count} box(es) to storage · ${data.transfer_id}`);
      printWarehouseTransfer(data);
      await fetchPending();
    } catch (e) {
      toast.error(e.message || 'Failed to transfer boxes');
    } finally {
      setTransferring(false);
    }
  };

  const reprint = async (transferId) => {
    try {
      const res = await fetch(`${API}/api/wms/packing/seasonal-boxes/transfer/${encodeURIComponent(transferId)}`, {
        headers: authHeaders(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'reprint failed');
      printWarehouseTransfer(data);
    } catch (e) {
      toast.error(e.message || 'Failed to reprint transfer');
    }
  };

  const hq = historySearch.trim().toLowerCase();
  const filteredHistory = hq
    ? history.filter((t) =>
        [t.transfer_id, ...(t.small_boxes || []), ...(t.box_numbers || [])]
          .some((v) => String(v || '').toLowerCase().includes(hq)))
    : history;

  const StatCard = ({ label, value, testid }) => (
    <div className="bg-white rounded-lg border border-[#E5DFD0] p-4">
      <p className="text-sm text-[#5A5F6E]">{label}</p>
      <p className="text-2xl font-bold text-[#1A1F2E]" data-testid={testid}>{value}</p>
    </div>
  );

  return (
    <div data-testid="seasonal-boxes-tab">
      {/* Sub-view toggle */}
      <div className="flex gap-2 mb-6">
        <Button
          onClick={() => setView('pending')}
          variant={view === 'pending' ? 'default' : 'outline'}
          className={view === 'pending' ? 'bg-[#2D5F4F] hover:bg-[#244C40]' : 'border-[#E5DFD0]'}
          data-testid="seasonal-subtab-pending"
        >
          <PackageCheck className="w-4 h-4 mr-2" /> Pending Transfer
        </Button>
        <Button
          onClick={() => setView('history')}
          variant={view === 'history' ? 'default' : 'outline'}
          className={view === 'history' ? 'bg-[#2D5F4F] hover:bg-[#244C40]' : 'border-[#E5DFD0]'}
          data-testid="seasonal-subtab-history"
        >
          <History className="w-4 h-4 mr-2" /> Transferred History
        </Button>
      </div>

      {view === 'pending' ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <StatCard label="Boxes" value={summary.count.toLocaleString('en-IN')} testid="seasonal-stat-boxes" />
            <StatCard label="Total Qty" value={summary.total_qty.toLocaleString('en-IN')} testid="seasonal-stat-qty" />
            <StatCard label="Total Value" value={inr(summary.total_value)} testid="seasonal-stat-value" />
          </div>

          <div className="bg-white rounded-lg border border-[#E5DFD0] overflow-hidden">
            <div className="px-4 py-3 bg-[#F8F4EE] border-b border-[#E5DFD0] flex flex-wrap items-center justify-between gap-3">
              <h2 className="font-semibold text-[#1A1F2E]">
                Packed Seasonal Boxes ({boxes.length})
              </h2>
              <div className="flex flex-wrap items-center gap-2">
                <Button size="sm" variant="outline" className="border-[#E5DFD0]" onClick={fetchPending} data-testid="seasonal-refresh-btn">
                  <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
                </Button>
                <Button
                  size="sm"
                  className="bg-[#2D5F4F] hover:bg-[#244C40]"
                  disabled={transferring || selectedIds.length === 0}
                  onClick={() => doTransfer(selectedIds)}
                  data-testid="seasonal-transfer-selected-btn"
                >
                  <Warehouse className="w-4 h-4 mr-2" /> Transfer to Storage ({selectedIds.length})
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-[#2D5F4F] text-[#2D5F4F] hover:bg-[#2D5F4F]/10"
                  disabled={transferring || boxes.length === 0}
                  onClick={() => doTransfer(boxes.map((b) => b.box_id))}
                  data-testid="seasonal-transfer-all-btn"
                >
                  <Warehouse className="w-4 h-4 mr-2" /> Transfer All
                </Button>
              </div>
            </div>

            {/* Scan bar — scan a box number to auto-tick its checkbox */}
            {boxes.length > 0 && (
              <div className="px-4 py-3 border-b border-[#E5DFD0] bg-white flex flex-wrap items-center gap-3">
                <form onSubmit={handleScan} className="flex items-center gap-2 flex-1 min-w-[260px] max-w-md">
                  <div className="relative flex-1">
                    <ScanLine className="w-4 h-4 text-[#2D5F4F] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                    <Input
                      ref={scanRef}
                      value={scanValue}
                      onChange={onScanChange}
                      placeholder="Scan box or small-box code (auto-adds)…"
                      className="pl-9 border-[#E5DFD0] focus-visible:ring-[#2D5F4F]"
                      autoFocus
                      autoComplete="off"
                      data-testid="seasonal-scan-input"
                    />
                  </div>
                  <Button type="submit" size="sm" variant="outline" className="border-[#2D5F4F] text-[#2D5F4F] hover:bg-[#2D5F4F]/10" data-testid="seasonal-scan-add-btn">
                    Add
                  </Button>
                </form>
                <span className="text-sm text-[#5A5F6E]" data-testid="seasonal-scan-count">
                  {selectedIds.length} of {boxes.length} box{boxes.length === 1 ? '' : 'es'} selected
                </span>
              </div>
            )}

            {loading ? (
              <div className="p-8 text-center">
                <RefreshCw className="w-8 h-8 animate-spin mx-auto text-[#2D5F4F]" />
                <p className="mt-2 text-[#5A5F6E]">Loading seasonal boxes...</p>
              </div>
            ) : boxes.length === 0 ? (
              <div className="p-8 text-center" data-testid="seasonal-empty">
                <Box className="w-12 h-12 mx-auto text-gray-300" />
                <p className="mt-2 text-[#5A5F6E]">No packed seasonal boxes awaiting transfer</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#FBF8F2] text-left text-[#5A5F6E]">
                      <th className="px-4 py-2 w-10">
                        <Checkbox checked={allChecked} onCheckedChange={toggleAll} data-testid="seasonal-select-all" aria-label="Select all" />
                      </th>
                      <th className="px-4 py-2">Box No</th>
                      <th className="px-4 py-2">Pick Slip</th>
                      <th className="px-4 py-2">Customer</th>
                      <th className="px-4 py-2 text-right">Qty</th>
                      <th className="px-4 py-2 text-right">Value</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E5DFD0]">
                    {boxes.map((b) => (
                      <tr key={b.box_id} className="hover:bg-[#FFFBF5]" data-testid={`seasonal-box-row-${b.box_id}`}>
                        <td className="px-4 py-3">
                          <Checkbox
                            checked={!!selected[b.box_id]}
                            onCheckedChange={() => toggleOne(b.box_id)}
                            data-testid={`seasonal-box-check-${b.box_id}`}
                            aria-label={`Select ${b.box_number}`}
                          />
                        </td>
                        <td className="px-4 py-3 font-medium text-[#1A1F2E]">
                          {b.box_number}
                          {b.external_box_no && (
                            <span className="block text-xs font-normal text-[#5A5F6E]" data-testid={`seasonal-box-extno-${b.box_id}`}>
                              Small-box: {b.external_box_no}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-[#1A1F2E]">{b.pick_slip_number}</span>
                          <span className="ml-2 px-2 py-0.5 rounded-full text-xs font-semibold bg-lime-100 text-lime-800 border border-lime-300">Seasonal</span>
                        </td>
                        <td className="px-4 py-3 text-[#5A5F6E]">{b.customer || '—'}</td>
                        <td className="px-4 py-3 text-right font-medium">{(b.total_qty || 0).toLocaleString('en-IN')}</td>
                        <td className="px-4 py-3 text-right" data-testid={`seasonal-box-value-${b.box_id}`}>{inr(b.value)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="bg-white rounded-lg border border-[#E5DFD0] overflow-hidden">
          <div className="px-4 py-3 bg-[#F8F4EE] border-b border-[#E5DFD0] flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-semibold text-[#1A1F2E]">Warehouse Transfers ({filteredHistory.length})</h2>
            <div className="flex items-center gap-2">
              <div className="relative">
                <ScanLine className="w-4 h-4 text-[#2D5F4F] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <Input
                  value={historySearch}
                  onChange={(e) => setHistorySearch(e.target.value)}
                  placeholder="Search by sticker / box / ref…"
                  className="pl-9 h-9 w-64 border-[#E5DFD0] focus-visible:ring-[#2D5F4F]"
                  autoComplete="off"
                  data-testid="seasonal-history-search"
                />
              </div>
              <Button size="sm" variant="outline" className="border-[#E5DFD0]" onClick={fetchHistory} data-testid="seasonal-history-refresh-btn">
                <RefreshCw className={`w-4 h-4 mr-2 ${historyLoading ? 'animate-spin' : ''}`} /> Refresh
              </Button>
            </div>
          </div>
          {historyLoading ? (
            <div className="p-8 text-center">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto text-[#2D5F4F]" />
              <p className="mt-2 text-[#5A5F6E]">Loading history...</p>
            </div>
          ) : filteredHistory.length === 0 ? (
            <div className="p-8 text-center" data-testid="seasonal-history-empty">
              <History className="w-12 h-12 mx-auto text-gray-300" />
              <p className="mt-2 text-[#5A5F6E]">{hq ? `No transfers match "${historySearch}"` : 'No seasonal boxes transferred yet'}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#FBF8F2] text-left text-[#5A5F6E]">
                    <th className="px-4 py-2">Transfer Ref</th>
                    <th className="px-4 py-2">Date</th>
                    <th className="px-4 py-2">By</th>
                    <th className="px-4 py-2">Small-Boxes</th>
                    <th className="px-4 py-2 text-right">Boxes</th>
                    <th className="px-4 py-2 text-right">Qty</th>
                    <th className="px-4 py-2 text-right">Value</th>
                    <th className="px-4 py-2 text-right">Print</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E5DFD0]">
                  {filteredHistory.map((t) => (
                    <tr key={t.transfer_id} className="hover:bg-[#FFFBF5]" data-testid={`seasonal-transfer-row-${t.transfer_id}`}>
                      <td className="px-4 py-3 font-medium text-[#1A1F2E]">{t.transfer_id}</td>
                      <td className="px-4 py-3 text-[#5A5F6E]">{fmtDate(t.transferred_at)}</td>
                      <td className="px-4 py-3 text-[#5A5F6E]">{t.transferred_by || '—'}</td>
                      <td className="px-4 py-3 text-[#5A5F6E] max-w-xs" data-testid={`seasonal-transfer-smallboxes-${t.transfer_id}`}>
                        {(t.small_boxes && t.small_boxes.length)
                          ? <span className="break-words">{t.small_boxes.join(', ')}</span>
                          : <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-4 py-3 text-right">{(t.box_count || 0).toLocaleString('en-IN')}</td>
                      <td className="px-4 py-3 text-right">{(t.total_qty || 0).toLocaleString('en-IN')}</td>
                      <td className="px-4 py-3 text-right">{inr(t.total_value)}</td>
                      <td className="px-4 py-3 text-right">
                        <Button size="sm" variant="outline" className="border-[#E5DFD0]" onClick={() => reprint(t.transfer_id)} data-testid={`seasonal-reprint-btn-${t.transfer_id}`}>
                          <Printer className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SeasonalBoxesTab;
